/**
 * Wall-clock time in WIT (Waktu Indonesia Timur, Asia/Jayapura), the zone of the Diocese of
 * Agats. SATU shows every time and decides every "today" and "this year" in WIT, whatever
 * zone the device is set to: a laptop still set to WIB or a browser on UTC would otherwise
 * print the wrong hour and, between midnight and 09.00 WIT, think it is still yesterday.
 *
 * WIT is UTC+9 all year (Indonesia keeps no daylight saving), so shifting the instant by nine
 * hours and reading the UTC fields gives the exact WIT wall clock without Intl time-zone data.
 */

export const WIT_OFFSET_MS = 9 * 60 * 60 * 1000

export interface WaktuWit {
  tahun: number
  /** 1 to 12. */
  bulan: number
  hari: number
  jam: number
  menit: number
  detik: number
}

function instant(at: Date | string | number): number | null {
  const ms = at instanceof Date ? at.getTime() : typeof at === 'number' ? at : Date.parse(at)
  return Number.isNaN(ms) ? null : ms
}

/** The WIT wall clock at an instant, or null for an unreadable input. */
export function waktuWit(at: Date | string | number): WaktuWit | null {
  const ms = instant(at)
  if (ms === null) return null
  const d = new Date(ms + WIT_OFFSET_MS)
  return {
    tahun: d.getUTCFullYear(),
    bulan: d.getUTCMonth() + 1,
    hari: d.getUTCDate(),
    jam: d.getUTCHours(),
    menit: d.getUTCMinutes(),
    detik: d.getUTCSeconds(),
  }
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** "2026-09-29": the WIT calendar date at an instant (the partial-date text format). */
export function tanggalWit(at: Date | string | number): string | null {
  const w = waktuWit(at)
  return w === null ? null : `${w.tahun}-${pad2(w.bulan)}-${pad2(w.hari)}`
}

/** The WIT year at an instant; NaN for an unreadable input. */
export function tahunWit(at: Date | string | number): number {
  return waktuWit(at)?.tahun ?? Number.NaN
}

/** "10.42": hours and minutes in WIT with the Indonesian dot; "—" for an unreadable input. */
export function jamWit(at: Date | string | number): string {
  const w = waktuWit(at)
  return w === null ? '—' : `${pad2(w.jam)}.${pad2(w.menit)}`
}

/** True when two instants fall on the same WIT calendar day. */
export function samaHariWit(a: Date | string | number, b: Date | string | number): boolean {
  const x = tanggalWit(a)
  return x !== null && x === tanggalWit(b)
}
